import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { CreditCard, Shield, Eye, EyeOff, Copy, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import apiService from '@/services/api';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  status: 'connected' | 'disconnected' | 'active';
  configured?: boolean;
}

interface Config {
  apiStatus: 'live' | 'test';
  secretKey: string;
  publishableKey: string;
  webhookUrl: string;
  webhookSecret: string;
  isActive?: boolean;
}

interface SelectedIntegration extends Integration {
  config?: Config;
}

const IntegrationsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editedConfig, setEditedConfig] = useState<Config | null>(null);
  
  // Track integration statuses dynamically
  const [integrationStatuses, setIntegrationStatuses] = useState<{[key: string]: 'connected' | 'disconnected' | 'active'}>({
    stripe: 'disconnected',
    revolut: 'disconnected',
    webhooks: 'disconnected',
  });

  const baseIntegrations: Integration[] = [
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Handles all customer payments and provider payouts.',
      icon: CreditCard,
      status: 'connected',
      configured: true,
    },
    {
      id: 'revolut',
      name: 'Revolut Merchant',
      description: 'Accepts payments through Revolut merchant account and instant settlements.',
      icon: CreditCard,
      status: 'connected',
      configured: true,
    },
    {
      id: 'webhooks',
      name: 'Webhooks',
      description: 'Configure real-time data events for external system synchronization.',
      icon: Shield,
      status: 'active',
      configured: true,
    },
  ];

  // Create integrations list with dynamic statuses
  const integrations = baseIntegrations.map(integration => ({
    ...integration,
    status: integrationStatuses[integration.id] || 'disconnected'
  }));

  const [selectedIntegration, setSelectedIntegration] = useState<SelectedIntegration | null>(null);

  // Fetch integration config from API
  useEffect(() => {
    const fetchIntegrationConfig = async () => {
      try {
        setLoading(true);
        const response = await apiService.request('/admin/integrations', 'GET');
        if (response.success && response.data) {
          // Determine connection status for each integration based on isActive flag
          const statuses: {[key: string]: 'connected' | 'disconnected' | 'active'} = {};
          
          // Check each integration's configuration status
          Object.keys(response.data).forEach(integrationType => {
            const config = response.data[integrationType];
            // Use isActive flag from API to determine status
            statuses[integrationType] = config.isActive ? 'connected' : 'disconnected';
          });
          
          setIntegrationStatuses(statuses);
          
          // Initialize with Stripe by default
          const stripeIntegration = baseIntegrations.find(i => i.id === 'stripe');
          if (stripeIntegration) {
            setSelectedIntegration({
              ...stripeIntegration,
              status: statuses.stripe || 'disconnected',
              config: {
                apiStatus: response.data.stripe?.apiStatus || 'live',
                secretKey: response.data.stripe?.secretKey || '••••••••••••••••••••••••••••••••',
                publishableKey: response.data.stripe?.publishableKey || '••••••••••••••••••••••••••••••••',
                webhookUrl: response.data.stripe?.webhookUrl || 'https://api.virewire.com/webhooks/stripe',
                webhookSecret: response.data.stripe?.webhookSecret || '••••••••••••••••••••••••••••••••',
                isActive: response.data.stripe?.isActive ?? true,
              }
            });
            setEditedConfig({
              apiStatus: response.data.stripe?.apiStatus || 'live',
              secretKey: response.data.stripe?.secretKey || '••••••••••••••••••••••••••••••••',
              publishableKey: response.data.stripe?.publishableKey || '••••••••••••••••••••••••••••••••',
              webhookUrl: response.data.stripe?.webhookUrl || 'https://api.virewire.com/webhooks/stripe',
              webhookSecret: response.data.stripe?.webhookSecret || '••••••••••••••••••••••••••••••••',
              isActive: response.data.stripe?.isActive ?? true,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch integrations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrationConfig();
  }, []);

  const handleConfigureClick = async (integration: Integration) => {
    try {
      setLoading(true);
      const response = await apiService.request(`/admin/integrations/${integration.id}`, 'GET');
      
      if (response.success && response.data) {
        const config = {
          apiStatus: response.data.apiStatus || 'live',
          secretKey: response.data.secretKey || '',
          publishableKey: response.data.publishableKey || '',
          webhookUrl: response.data.webhookUrl || `https://api.virewire.com/webhooks/${integration.id}`,
          webhookSecret: response.data.webhookSecret || '',
          isActive: response.data.isActive ?? true,
        };
        
        // Determine connection status based on isActive flag
        const status: 'connected' | 'disconnected' = response.data.isActive ? 'connected' : 'disconnected';
        
        // Update integration status
        setIntegrationStatuses(prev => ({
          ...prev,
          [integration.id]: status
        }));
        
        setSelectedIntegration({ ...integration, config, status });
        setEditedConfig(config);
      }
    } catch (error) {
      console.error('Failed to fetch integration config:', error);
      setMessage({ type: 'error', text: 'Failed to load integration configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setMessage(null);
      
      const response = await apiService.request(`/admin/integrations/£{selectedIntegration?.id}/test`, 'POST', {
        apiStatus: editedConfig?.apiStatus || 'live',
      });

      if (response.success) {
        setMessage({ type: 'success', text: `✅ £{selectedIntegration?.name} connection successful!` });
      } else {
        setMessage({ type: 'error', text: `❌ Connection failed: £{response.message}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: `❌ Connection test failed: £{error.message}` });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSavingChanges(true);
      setMessage(null);

      const response = await apiService.request(`/admin/integrations/£{selectedIntegration?.id}`, 'PUT', {
        apiStatus: editedConfig?.apiStatus || 'live',
        secretKey: editedConfig?.secretKey || '',
        publishableKey: editedConfig?.publishableKey || '',
        webhookUrl: editedConfig?.webhookUrl || '',
        webhookSecret: editedConfig?.webhookSecret || '',
        isActive: true
      });

      if (response.success) {
        // When we save with isActive: true, the integration is now connected
        const newStatus: 'connected' | 'disconnected' = 'connected';
        
        // Update integration status
        if (selectedIntegration) {
          setIntegrationStatuses(prev => ({
            ...prev,
            [selectedIntegration.id]: newStatus
          }));
          
          setSelectedIntegration(prev => prev ? { ...prev, status: newStatus } : null);
        }
        
        setMessage({ type: 'success', text: `✅ £{selectedIntegration?.name} configuration saved successfully!` });
      } else {
        setMessage({ type: 'error', text: `❌ Failed to save: £{response.message}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: `❌ Save failed: £{error.message}` });
    } finally {
      setSavingChanges(false);
    }
  };

  const handleClearConfiguration = async () => {
    if (!window.confirm(`⚠️ Are you sure you want to clear all configuration for £{selectedIntegration?.name}? This will disable the integration.`)) {
      return;
    }

    try {
      setSavingChanges(true);
      setMessage(null);
      
      const response = await apiService.request(`/admin/integrations/£{selectedIntegration?.id}`, 'PUT', {
        apiStatus: 'live',
        secretKey: '', // Clear
        publishableKey: '', // Clear
        webhookUrl: editedConfig?.webhookUrl || '',
        webhookSecret: '', // Clear
        isActive: false // Disable
      });

      if (response.success) {
        // Status changed to disconnected
        const newStatus: 'disconnected' = 'disconnected';
        
        if (selectedIntegration) {
          setIntegrationStatuses(prev => ({
            ...prev,
            [selectedIntegration.id]: newStatus
          }));
          
          setSelectedIntegration(prev => prev ? { ...prev, status: newStatus } : null);
        }
        
        // Clear the form
        setEditedConfig({
          apiStatus: 'live',
          secretKey: '',
          publishableKey: '',
          webhookUrl: editedConfig?.webhookUrl || `https://api.virewire.com/webhooks/${selectedIntegration?.id}`,
          webhookSecret: '',
          isActive: false,
        });
        
        setMessage({ type: 'success', text: `✅ ${selectedIntegration?.name} configuration cleared! Status changed to Disabled.` });
      } else {
        setMessage({ type: 'error', text: `❌ Failed to clear configuration: ${response.message}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: `❌ Clear failed: ${error.message}` });
    } finally {
      setSavingChanges(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: '✅ Copied to clipboard!' });
    setTimeout(() => setMessage(null), 2000);
  };

  // Helper function to check if a value is masked
  const isMasked = (value: string): boolean => {
    return value?.startsWith('•') || value === '••••••••••••••••••••••••••••••••';
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-700';
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'disconnected':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disabled';
      case 'active':
        return 'Active';
      default:
        return status.toUpperCase();
    }
  };

  if (loading && selectedIntegration === null) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading integrations...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Integrations</h1>
          <p className="text-sm text-gray-600">
            Manage your platform's payment processors and webhook configurations
          </p>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Integrations Grid */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-6">
              {integrations.map((integration) => {
                const IconComponent = integration.icon;
                return (
                  <div
                    key={integration.id}
                    className={`bg-white rounded-lg border p-6 hover:shadow-lg transition-shadow cursor-pointer ${
                      selectedIntegration?.id === integration.id
                        ? 'border-teal-600 shadow-lg'
                        : 'border-gray-200'
                    }`}
                    onClick={() => handleConfigureClick(integration)}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <IconComponent className="w-6 h-6 text-gray-900" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              integration.status === 'connected'
                                ? 'bg-green-500'
                                : 'bg-gray-400'
                            }`}
                          />
                          <span
                            className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${getStatusBadgeColor(
                              integration.status
                            )}`}
                          >
                            {getStatusLabel(integration.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-1">{integration.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">{integration.description}</p>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConfigureClick(integration);
                      }}
                      className="w-full px-4 py-2 border border-teal-600 text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-colors"
                    >
                      Configure
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Configuration Panel */}
          {selectedIntegration && editedConfig && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-8">
                <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <div className="p-2 bg-gray-100 rounded">
                    {<selectedIntegration.icon className="w-5 h-5 text-gray-900" />}
                  </div>
                  {selectedIntegration.name}
                </h2>
                <p className="text-xs text-gray-500 font-medium mb-6">
                  {selectedIntegration.status === 'disconnected'
                    ? 'Service Integration'
                    : 'Payment Processing Unit'}
                </p>

                {/* Message Display */}
                {message && (
                  <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
                    message.type === 'success'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    {message.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <p className={`text-sm ${
                      message.type === 'success' ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Configuration Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-xs text-blue-700">
                      <strong>ℹ️ Configuration Settings:</strong> Edit your API keys, webhook URLs, and signing secrets. Changes are saved to the database when you click "Save Changes".
                    </p>
                  </div>

                  {/* API Status - LIVE/TEST MODE SWITCHER */}
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-500 mb-3">API Status</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setEditedConfig({ ...editedConfig, apiStatus: 'live' })}
                        className={`flex-1 px-3 py-2 font-semibold rounded-lg text-sm transition-colors border ${
                          editedConfig.apiStatus === 'live'
                            ? 'bg-teal-50 text-teal-700 border-teal-600'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        Live Mode
                      </button>
                      <button 
                        onClick={() => setEditedConfig({ ...editedConfig, apiStatus: 'test' })}
                        className={`flex-1 px-3 py-2 font-semibold rounded-lg text-sm transition-colors border ${
                          editedConfig.apiStatus === 'test'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-600'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        Test Mode
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Current: <strong>{editedConfig.apiStatus === 'live' ? '🔴 LIVE' : '🟡 TEST'}</strong></p>
                  </div>

                  {/* Secret Key - EDITABLE */}
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-500 mb-2">Secret Key</p>
                    <input
                      type="text"
                      value={editedConfig.secretKey}
                      onChange={(e) => setEditedConfig({ ...editedConfig, secretKey: e.target.value })}
                      placeholder="Enter Stripe secret key (sk_test_...)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  {/* Publishable Key - EDITABLE */}
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-500 mb-2">Publishable Key</p>
                    <input
                      type="text"
                      value={editedConfig.publishableKey}
                      onChange={(e) => setEditedConfig({ ...editedConfig, publishableKey: e.target.value })}
                      placeholder="Enter publishable key (pk_...)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  {/* Webhook URL */}
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-500 mb-2">Webhook URL</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editedConfig.webhookUrl}
                        onChange={(e) => setEditedConfig({ ...editedConfig, webhookUrl: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-white"
                      />
                      <button
                        onClick={() => copyToClipboard(editedConfig.webhookUrl || '')}
                        className="px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                        title="Copy webhook URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Use this webhook URL with your service provider</p>
                  </div>

                  {/* Webhook Signing Secret - EDITABLE */}
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-500 mb-2">Webhook Signing Secret</p>
                    <input
                      type="text"
                      value={editedConfig.webhookSecret}
                      onChange={(e) => setEditedConfig({ ...editedConfig, webhookSecret: e.target.value })}
                      placeholder="Enter webhook signing secret (whsec_...)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-4 border-t border-gray-200">
                    <button 
                      onClick={handleSaveChanges}
                      disabled={savingChanges}
                      className="w-full px-4 py-2 bg-teal-700 hover:bg-teal-800 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {savingChanges ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {savingChanges ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button 
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      className="w-full px-4 py-2 border border-teal-600 text-teal-600 hover:bg-teal-50 disabled:bg-gray-100 disabled:text-gray-500 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {testingConnection ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {testingConnection ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button 
                      onClick={handleClearConfiguration}
                      disabled={savingChanges}
                      className="w-full px-4 py-2 border border-red-400 text-red-600 hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-500 font-semibold rounded-lg transition-colors"
                    >
                      {savingChanges ? 'Clearing...' : '🗑️ Clear Configuration'}
                    </button>
                    <button
                      onClick={() => navigate(`/admin/configuration-status?view=audit-log&entity=integration${selectedIntegration?.id ? `&id=${selectedIntegration.id}` : ''}`)}
                      className="w-full px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold rounded-lg transition-colors"
                    >
                      View Logs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default IntegrationsPage;
